import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronRight, Plus, FileText, Pencil, Box, User, CalendarDays, CalendarPlus, Briefcase } from "lucide-react";
import EmptyState from "../components/EmptyState";
import { Button, Spinner, Field } from "../components/ui/kit";
import { TechEkonomika } from "../components/TechEkonomika";
import TechKroky from "../components/TechKroky";
import { NabidkaZeSablonyDialog } from "../components/SablonyDialogy";
import { etapa, fazeBarva, nazevEtapy } from "../lib/faze";
import { NStatusPill, N_STATUS_LABEL, nStav } from "../lib/nabidky";
import { fmtKc } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { canSee, subObchod } from "../lib/nav";
import api, { apiError } from "../lib/api";
import { Drobecky, useNazevStranky, useUrlStav } from "../lib/navigace";
import { useJeMobil } from "../lib/mobil";
import { ikonaTechnologie } from "../lib/technologie";
import { SkupinyAktivit } from "../lib/aktivity";
import DetailHlava, { KartaZalozky, KartaSekce, Pridat, StitekTmavy } from "../components/DetailHlava";
import RadekAktivity from "../components/RadekAktivity";
import PasFazi from "../components/PasFazi";
import AktivitaDetail from "../components/AktivitaDetail";
import NovaAktivita from "../components/NovaAktivita";

// Údaje ze zaměření. Povinná pro nabídku (místo, výkon) vrací server
// v `zamereni_povinna` — bez nich technologie do fáze Nabídka nepřejde.
const UDAJE_POLE = [
  ["misto_instalace", "Umístění v objektu"], ["vykon_rozsah", "Výkon / rozsah"], ["zarizeni", "Zařízení"],
  ["rozvody", "Rozvody"], ["termin_realizace", "Termín realizace"], ["dotace", "Dotace"],
];

// Aktivity technologie vznikají samy podle workflow (Nastavení → Workflow
// úkolů) a část se sama odškrtne, když se stane, co úkol hlídá.

export default function TechDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const mobil = useJeMobil();
  // Subdodavatel s přístupem do obchodu nabídky vidí a upravuje (bez nákupních cen).
  const canOffer = canSee(user, ["OWNER", "OBCHODNIK"]) || subObchod(user);
  const smiSablony = canSee(user, ["OWNER", "OBCHODNIK"]);
  const isOwner = canSee(user, ["OWNER"]);
  // Údaje ze zaměření doplní i technik (server mu dovolí jen tato pole).
  const canZamereni = canSee(user, ["OWNER", "OBCHODNIK", "TECHNIK"]);
  const [data, setData] = useState(undefined);
  const [tabUrl, setTab] = useUrlStav("tab", "nabidky");
  const [nabidky, setNabidky] = useState([]);
  const [creating, setCreating] = useState(false);
  const [zeSablony, setZeSablony] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [editAct, setEditAct] = useState(null);
  const [novaAkt, setNovaAkt] = useState(false);
  const [upravaUdaju, setUpravaUdaju] = useState(0);   // zvýšením se otevře úprava údajů

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/api/technologie/${id}`); setData(data); }
    catch { setData(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useNazevStranky(data?.technologie && [data.technologie.kategorie_name, data.pripad?.nazev].filter(Boolean).join(" · "));
  useEffect(() => {
    if (canOffer) api.get("/api/nabidky", { params: { technologie_id: id } }).then((r) => setNabidky(r.data.items || [])).catch(() => {});
  }, [id, canOffer]);

  const createNabidka = async () => {
    setCreating(true);
    try { const { data } = await api.post(`/api/technologie/${id}/nabidky`, {}); nav(`/nabidky/${data.id}`); }
    catch (e) { window.alert(apiError(e.response?.data?.detail) || e.message); }
    finally { setCreating(false); }
  };

  if (data === undefined) return <div className="p-10 flex justify-center text-muted"><Spinner /></div>;
  if (data === null) return <div className="card p-8"><EmptyState icon="PackageX" title="Technologie nenalezena" action={<Button variant="ghost" onClick={() => nav("/obchod")}>Zpět</Button>} /></div>;

  const t = data.technologie;
  const money = data.muze_ceny;
  const ztracena = t.faze === "ZTRACENO";
  const realizaceLock = data.realizace_odemceno ? null : ztracena ? "Technologie je ztracená" : "Otevře se po zahájení montáže";
  // Založit nabídku jde, jen když to dovolí brána (zaměření) — rozhoduje server.
  const kroky = data.kroky || { akce: [], fakta: null };
  // Založit (další) nabídku — variantu pro klienta — dovolí server (nova_nabidka).
  const lzeNabidku = data.nova_nabidka ? data.nova_nabidka.ok : kroky.akce.some((a) => a.id === "VYTVORIT_NABIDKU" && a.ok);
  // Varianty: do schválení může mít technologie víc živých nabídek. „Ta“ nabídka
  // je schválená, jinak jediná živá; víc živých = klient vybírá z variant.
  const zive = nabidky.filter((n) => ["ROZPRACOVANA", "ODESLANA"].includes(n.status));
  const schvalena = nabidky.find((n) => n.status === "SCHVALENA");
  const zivaNabidka = schvalena || (zive.length === 1 ? zive[0] : null);
  const variant = !schvalena && zive.length > 1 ? zive.length : 0;
  const Icon = ikonaTechnologie(t.kategorie_icon);
  const ukoly = data.ukoly || [];
  const otevreneUkoly = ukoly.filter((a) => a.stav === "OTEVRENA").length;
  const prepniUkol = async (a) => {
    try { await api.post(`/api/aktivity/${a.id}/hotovo`); load(); }
    catch (e) { window.alert(apiError(e.response?.data?.detail) || e.message); }
  };
  const zalozky = [
    ...(canOffer ? [{ key: "nabidky", label: "Nabídky", pocet: nabidky.length, testid: "tech-tab-nabidky" }] : []),
    { key: "prehled", label: "Údaje ze zaměření", testid: "tech-tab-prehled" },
    // Přijaté faktury (projekt, zaměření) jdou nahrát kdykoli; plán a marže se ukážou po schválení nabídky.
    ...(money ? [{ key: "ekonomika", label: "Ekonomika", testid: "tech-tab-ekonomika" }] : []),
    { key: "realizace", label: "Montáž", zamceno: realizaceLock, testid: "tech-tab-realizace" },
  ];
  // Záložka z adresy (?tab=ekonomika z přehledu, nebo po návratu zpět) — zamčená
  // nebo bez práva ji vidět ne. Výchozí: nabídky (kdo je smí vidět), jinak údaje.
  const aktivniTab = zalozky.some((z) => z.key === tabUrl && !z.zamceno) ? tabUrl : canOffer ? "nabidky" : "prehled";
  const fz = fazeBarva(etapa(t));

  return (
    <div data-testid="tech-detail" className="flex flex-col gap-4">
      <Drobecky aktualni={t.kategorie_name}
        cesta={[{ nazev: "Obchodní případy", to: "/obchod" },
          ...(data.pripad ? [{ nazev: `${data.pripad.cislo} ${data.pripad.nazev}`, to: `/obchod/${data.pripad.id}`, testid: "tech-zpet" }] : [])]} />

      <DetailHlava testid="tech-hlava" druh={data.pripad ? `Technologie · ${data.pripad.cislo} ${data.pripad.nazev}` : "Technologie"}
        nazev={t.kategorie_name}
        obrazek={<span className="h-12 w-12 md:h-14 md:w-14 rounded-[16px] bg-white/10 text-white flex items-center justify-center"><Icon size={24} strokeWidth={1.8} /></span>}
        stitky={<StitekTmavy testid="tech-faze" barva={ztracena ? "#ff8a7a" : fz.dot}>{nazevEtapy(etapa(t))}</StitekTmavy>}
        meta={[
          <span className="inline-flex items-center gap-1.5"><Box size={13} />{t.vyrobce || "výrobce neuveden"}</span>,
          data.pripad?.client_name && (
            <button onClick={() => nav(`/klienti/${data.pripad.client_id}`)} className="inline-flex items-center gap-1.5 font-600 text-white hover:text-white/80"
              data-testid="tech-klient"><User size={13} />{data.pripad.client_name}</button>
          ),
          [t.zarizeni, t.vykon_rozsah].filter(Boolean).length > 0 && <span>{[t.zarizeni, t.vykon_rozsah].filter(Boolean).join(" · ")}</span>,
          <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} />{t.termin_realizace ? `Termín ${t.termin_realizace}` : "Termín realizace neuveden"}</span>,
        ].filter(Boolean)}
        vedlejsi={!zive.length && !schvalena && smiSablony && lzeNabidku ? [{ text: "Ze šablony", onClick: () => setZeSablony(true), testid: "nabidka-ze-sablony-btn" }] : []}
        hlavni={variant
          ? { text: `Varianty nabídky (${variant})`, ikona: FileText, onClick: () => setTab("nabidky"), testid: "tech-varianty" }
          : zivaNabidka
          ? { text: "Otevřít nabídku", ikona: FileText, onClick: () => nav(`/nabidky/${zivaNabidka.id}`), testid: "tech-otevrit-nabidku" }
          : lzeNabidku && canOffer ? { text: creating ? "Zakládám…" : "Vytvořit nabídku", ikona: Plus, onClick: createNabidka, disabled: creating, testid: "create-nabidka" } : null}
        vice={[
          ...(mobil && smiSablony && lzeNabidku && !zive.length && !schvalena ? [{ text: "Nabídka ze šablony", ikona: FileText, onClick: () => setZeSablony(true), testid: "tech-vice-sablona" }] : []),
          ...(canZamereni ? [{ text: "Upravit údaje ze zaměření", ikona: Pencil, onClick: () => { setTab("prehled"); setUpravaUdaju((x) => x + 1); }, testid: "tech-vice-udaje" }] : []),
          ...(data.pripad ? [{ text: "Otevřít obchodní případ", ikona: Briefcase, onClick: () => nav(`/obchod/${data.pripad.id}`), testid: "tech-vice-pripad" }] : []),
        ]}
        cta={[
          ...(variant ? [{ text: "Varianty", ikona: FileText, onClick: () => setTab("nabidky"), testid: "tech-cta-varianty" }]
            : zivaNabidka ? [{ text: "Nabídka", ikona: FileText, onClick: () => nav(`/nabidky/${zivaNabidka.id}`), testid: "tech-cta-nabidka" }]
            : lzeNabidku && canOffer ? [{ text: "Nabídka", ikona: Plus, onClick: createNabidka, testid: "tech-cta-vytvorit" }] : []),
          { text: "Aktivita", ikona: CalendarPlus, onClick: () => setNovaAkt(true), testid: "tech-cta-aktivita" },
          ...(canZamereni ? [{ text: nazevEtapy("ZAMERENI"), ikona: Pencil, onClick: () => { setTab("prehled"); setUpravaUdaju((x) => x + 1); }, testid: "tech-cta-udaje" }] : []),
        ]}
        dlazdice={[
          ...(money ? [{ popisek: "Hodnota", hodnota: fmtKc(t.hodnota_kc || 0), jednotka: "Kč", testid: "tech-hodnota",
            pod: t.hodnota_z_nabidky ? "ze schválené nabídky" : "odhad" }] : []),
          { popisek: "Kde to stojí", hodnota: nazevEtapy(etapa(t)), zvyraznit: ztracena ? "danger" : undefined, testid: "tech-dl-faze",
            pod: ztracena ? "technologie je ztracená" : !data.ekonomika_odemceno ? "marže po schválení nabídky" : realizaceLock ? "montáž ještě nezačala" : "montáž běží" },
          ...(canOffer ? [variant
            ? { popisek: "Nabídka", hodnota: `${variant} ${variant < 5 ? "varianty" : "variant"}`, testid: "tech-dl-nabidka", pod: "klient vybírá", onClick: () => setTab("nabidky") }
            : { popisek: "Nabídka", hodnota: zivaNabidka ? N_STATUS_LABEL[nStav(zivaNabidka)] || zivaNabidka.status : "žádná", testid: "tech-dl-nabidka",
              pod: zivaNabidka ? `${zivaNabidka.cislo}${money ? ` · ${fmtKc(zivaNabidka.celkem_s_dph)} Kč s DPH` : ""}` : "vytvoř ji po zaměření",
              onClick: zivaNabidka ? () => nav(`/nabidky/${zivaNabidka.id}`) : undefined }] : []),
          { popisek: "Otevřené aktivity", hodnota: String(otevreneUkoly), testid: "tech-dl-aktivity",
            zvyraznit: ukoly.some((a) => a.po_terminu) ? "danger" : undefined,
            pod: `${ukoly.length - otevreneUkoly} hotových` },
        ]} />

      {/* kde to stojí + další krok — přes celou šířku */}
      <div className="card px-4 md:px-5 py-4" data-testid="tech-kde-to-stoji">
        <div className="flex items-center gap-3 mb-3">
          <h2 className="t-sekce text-ink font-700">Kde to stojí</h2>
          <span className="t-meta text-muted-2 hidden md:inline">fáze technologie</span>
        </div>
        <PasFazi faze={etapa(t)}
          kdy={{ ...(t.faze_kdy || {}), ...Object.fromEntries(Object.entries(t.mezikrok_kdy || {}).map(([id, d]) => [`M:${id}`, d])) }} />
        {(t.bez_zamereni || (ztracena && t.ztraceno)) && (
          <div className="mt-2 flex flex-wrap gap-x-4 t-meta">
            {t.bez_zamereni && <span className="text-muted">Bez zaměření: {t.bez_zamereni.duvod}</span>}
            {ztracena && t.ztraceno && (
              <span className="text-danger" data-testid="duvod-ztraty">Ztraceno: {(data.duvody_ztraty || {})[t.ztraceno.duvod] || t.ztraceno.duvod}{t.ztraceno.poznamka ? ` — ${t.ztraceno.poznamka}` : ""}</span>
            )}
          </div>
        )}
      </div>
      {!ztracena && (
        <TechKroky tech={{ ...t, akce: kroky.akce }} pripad={data.pripad} fakta={kroky.fakta}
          nabidkaId={(zivaNabidka || zive[0])?.id} isOwner={isOwner} onChanged={load} />
      )}

      {/* Počítač: vlevo záložky, vpravo aktivity. Telefon: aktivity hned pod dalším krokem. */}
      <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-4 items-start">
        <div className="min-w-0 lg:col-start-2 lg:row-start-1">
          <KartaSekce testid="ukoly-technologie" titul="Aktivity" pocet={ukoly.length || null}
            akce={<Pridat text="Aktivita" onClick={() => setNovaAkt(true)} testid="tech-nova-aktivita" />}>
            {ukoly.length === 0 ? (
              <p className="px-4 md:px-5 pb-4 t-meta text-muted">Zatím žádné. Vzniknou samy podle fáze technologie.</p>
            ) : (
              <div className="border-t border-line">
                <SkupinyAktivit items={ukoly} render={(a) => (
                  <RadekAktivity key={a.id} kompakt a={{ ...a, osoby_jmena: a.osoby_jmena }} onOpen={() => setDetailId(a.id)}
                    onToggle={() => prepniUkol(a)} />
                )} />
              </div>
            )}
          </KartaSekce>
        </div>

        <div className="min-w-0 lg:col-start-1 lg:row-start-1">
          <KartaZalozky testid="tech-zalozky" aktivni={aktivniTab} onZmena={setTab} zalozky={zalozky}
            akce={aktivniTab === "nabidky" && canOffer && lzeNabidku
              ? <Pridat text={creating ? "Zakládám…" : zive.length ? "Varianta" : "Nabídka"} onClick={createNabidka} testid="tech-tab-pridat-nabidku" />
              : aktivniTab === "prehled" && canZamereni
                ? <Pridat text="Upravit" onClick={() => setUpravaUdaju((x) => x + 1)} testid="tech-tab-upravit-udaje" />
                : null}>
            {aktivniTab === "nabidky" && (
              <div className="rise">
                {nabidky.length === 0 ? (
                  <EmptyState icon="FileText" title="Zatím žádná nabídka"
                    description={lzeNabidku || !data.nova_nabidka?.duvod ? "Vytvoř nabídku a naceň materiál, práci a dopravu." : data.nova_nabidka.duvod}
                    action={lzeNabidku && canOffer ? <Button onClick={createNabidka} disabled={creating} data-testid="tech-prazdne-vytvorit">
                      <Plus size={15} /> {creating ? "Zakládám…" : "Vytvořit nabídku"}</Button> : null} />
                ) : nabidky.map((n) => (
                  <button key={n.id} onClick={() => nav(`/nabidky/${n.id}`)} data-testid={`tech-nabidka-${n.id}`}
                    className="w-full text-left flex items-center gap-3.5 px-4 md:px-5 py-3 border-b border-line-soft last:border-0 hover:bg-inputbg">
                    <span className="h-9 w-9 rounded-[12px] bg-[#eef4fd] text-[#24589f] inline-flex items-center justify-center shrink-0"><FileText size={16} strokeWidth={1.9} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block t-telo font-600 text-ink truncate">{n.nazev}</span>
                      <span className="block t-meta text-muted truncate"><span className="amount">{n.cislo}</span>{n.verze ? ` · verze ${n.verze}` : ""}</span>
                    </span>
                    {money && <span className="amount t-telo font-700 text-ink hidden sm:block">{fmtKc(n.celkem_s_dph)} Kč</span>}
                    <NStatusPill status={nStav(n)} />
                    <ChevronRight size={15} className="text-muted-2 shrink-0" />
                  </button>
                ))}
              </div>
            )}
            {aktivniTab === "prehled" && (
              <div className="rise" data-testid="tech-prehled">
                <UdajeTechnologie t={t} onSaved={load} muzeUpravit={canZamereni} povinna={data.zamereni_povinna || []} otevrit={upravaUdaju} />
              </div>
            )}
            {aktivniTab === "ekonomika" && (
              <div className="rise p-3 md:p-4 bg-[#fafaf8]"><TechEkonomika techId={id} pripadId={data.pripad?.id} /></div>
            )}
            {aktivniTab === "realizace" && (
              <div className="rise">
                <EmptyState icon="Wrench" title="Montáž běží"
                  description="Faktury, materiál a výdejky k této technologii najdeš v záložce Ekonomika." />
              </div>
            )}
          </KartaZalozky>
        </div>
      </div>

      {zeSablony && <NabidkaZeSablonyDialog tech={t} onClose={() => setZeSablony(false)} />}
      {novaAkt && <NovaAktivita technologieId={id} pripadId={data.pripad?.id} defaultClientId={data.pripad?.client_id}
        onClose={() => setNovaAkt(false)} onSaved={() => { setNovaAkt(false); load(); }} />}
      {detailId && (
        <AktivitaDetail id={detailId} onClose={() => setDetailId(null)}
          onEdit={(x) => { setDetailId(null); setEditAct(x); }} onChanged={load} />
      )}
      {editAct && (
        <NovaAktivita aktivita={editAct} onClose={() => setEditAct(null)} onSaved={() => { setEditAct(null); load(); }} />
      )}
    </div>
  );
}

function UdajeTechnologie({ t, onSaved, muzeUpravit, povinna = [], otevrit = 0 }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const start = () => {
    const f = {};
    UDAJE_POLE.forEach(([k]) => { f[k] = t[k] || ""; });
    f.poznamka_pro_montery = t.poznamka_pro_montery || "";
    setForm(f); setEdit(true);
  };
  const [chyba, setChyba] = useState("");
  const save = async () => {
    setSaving(true); setChyba("");
    try { await api.patch(`/api/technologie/${t.id}`, form); setEdit(false); onSaved && onSaved(); }
    catch (e) { setChyba(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  // „Upravit" v hlavičce nebo v záložce otevře úpravu (počítadlo se zvýší).
  useEffect(() => { if (otevrit && muzeUpravit) start(); }, [otevrit]); // eslint-disable-line react-hooks/exhaustive-deps
  const filled = UDAJE_POLE.filter(([k]) => t[k]);
  const hasAny = filled.length > 0 || t.poznamka_pro_montery;

  if (edit) {
    return (
      <div className="p-4 md:p-5 space-y-4" data-testid="udaje-edit">
        <div className="flex items-center justify-between">
          <span className="t-meta text-muted">Úprava údajů ze zaměření</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setEdit(false)}>Zrušit</Button>
            <Button onClick={save} disabled={saving} data-testid="udaje-save">{saving ? <Spinner /> : "Uložit"}</Button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {UDAJE_POLE.map(([k, label]) => (
            <Field key={k} label={povinna.includes(k) ? `${label} *` : label}
              hint={povinna.includes(k) ? "Povinné pro nabídku" : null}
              inputProps={{ value: form[k] || "", onChange: (e) => setForm({ ...form, [k]: e.target.value }), "data-testid": `udaje-${k}` }} />
          ))}
        </div>
        <div>
          <span className="label-up text-muted block mb-1.5">Poznámka pro montéry</span>
          <textarea className="field min-h-[80px] py-2" value={form.poznamka_pro_montery || ""}
            onChange={(e) => setForm({ ...form, poznamka_pro_montery: e.target.value })} data-testid="udaje-poznamka" />
        </div>
        {chyba && <div className="t-meta text-danger">{chyba}</div>}
      </div>
    );
  }
  return (
    <div className="p-4 md:p-5" data-testid="udaje-technologie">
      {muzeUpravit && <button onClick={start} className="sr-only" data-testid="udaje-edit-btn">Upravit údaje</button>}
      {!hasAny ? (
        muzeUpravit
          ? <button onClick={start} className="text-[13px] text-muted hover:text-ink">Klikni sem a doplň údaje (zařízení, výkon, rozvody, dotace…).</button>
          : <p className="text-[13px] text-muted">Údaje zatím nikdo nedoplnil.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {filled.map(([k, label]) => (
              <div key={k}><span className="label-up text-muted block mb-1">{label}</span><span className="text-[13.5px] font-600 text-ink">{t[k]}</span></div>
            ))}
          </div>
          {t.poznamka_pro_montery && <div><span className="label-up text-muted block mb-1">Poznámka pro montéry</span><p className="text-[13px] text-sec whitespace-pre-wrap">{t.poznamka_pro_montery}</p></div>}
        </div>
      )}
      {t.poznamky && <div className="mt-4 pt-4 border-t border-line-soft"><span className="label-up text-muted block mb-1">Poznámky</span><p className="text-[13px] text-sec whitespace-pre-wrap">{t.poznamky}</p></div>}
    </div>
  );
}
